import config from '../config';
import { toRaw } from 'vue';

const appConfig = config();
const { v2 } = appConfig.apiVersion;
const { apiPrefix, baseURL } = appConfig;

export default class TicketGroupService {
  constructor(client) {
    this.client = client;
  }

  async getTicketGroups(query = '') {
    const queryString =
      typeof query === 'string'
        ? query
        : new URLSearchParams(toRaw(query)).toString();
    return this.client.get(
      `${apiPrefix}${v2}/ticket-groups${queryString ? `?${queryString}` : ''}`
    );
  }

  async findOne(ticketGroupId) {
    return this.client.get(`${apiPrefix}${v2}/ticket-groups/${ticketGroupId}`);
  }

  async getMassiveIncorporationTickets() {
    return this.client.get(`${apiPrefix}${v2}/ticket-groups?type=Alta`);
  }

  async getTicketGroupTickets(ticketGroupId, params = {}) {
    const formattedParams = JSON.parse(JSON.stringify(toRaw(params)));
    return this.client.get(
      `${apiPrefix}${v2}/ticket-groups/${ticketGroupId}/tickets`,
      { params: formattedParams }
    );
  }

  async getTicketGroupOptionalFiles(ticketGroupId) {
    return this.client.get(
      `${apiPrefix}${v2}/ticket-groups/${ticketGroupId}/optional-files`
    );
  }

  async postRegistrationFile(payload) {
    const response = await this.client.post(
      `${apiPrefix}${v2}/ticket-groups/file-registration`,
      payload
    );
    return response.data;
  }

  async postRegistrationForm(payload) {
    const response = await this.client.post(
      `${apiPrefix}${v2}/ticket-groups/massive-form-registration`,
      payload
    );
    return response.data;
  }

  async getMassiveIncorporationDetail(ticketGroupId) {
    const response = await this.client.get(
      `${apiPrefix}${v2}/ticket-groups/${ticketGroupId}`
    );
    return response.data;
  }

  async getMassiveIncorporationFiles(ticketGroupId) {
    const response = await this.client.get(
      `${apiPrefix}${v2}/ticket-groups/${ticketGroupId}/files`
    );
    return response.data;
  }

  async precancelMassiveIncorporation(ticketGroupId) {
    const response = await this.client.post(
      `${apiPrefix}${v2}/ticket-groups/${ticketGroupId}/registration/pre-cancel`
    );
    return response.data;
  }

  async cancelMassiveIncorporation(ticketGroupId, payload) {
    const response = await this.client.post(
      `${apiPrefix}${v2}/ticket-groups/${ticketGroupId}/registration/cancel`,
      payload
    );
    return response.data;
  }

  async presuccessMassiveIncorporation(ticketGroupId) {
    const response = await this.client.post(
      `${apiPrefix}${v2}/ticket-groups/${ticketGroupId}/registration/pre-success`
    );
    return response.data;
  }

  async successMassiveIncorporation(ticketGroupId, payload) {
    const response = await this.client.post(
      `${apiPrefix}${v2}/ticket-groups/${ticketGroupId}/registration/success`,
      payload
    );
    return response.data;
  }

  getMassiveIncorporationForm(companyId, insuranceCompanyId) {
    return `${baseURL}${apiPrefix}${v2}/ticket-groups/registration/download-format?company=${companyId}&insurance=${insuranceCompanyId}`;
  }

  getMassiveIncorporationToEmployeeFormat(companyId, insuranceCompanyId) {
    return `${baseURL}${apiPrefix}${v2}/ticket-groups/registration/download-format-employee?company=${companyId}&insurance=${insuranceCompanyId}`;
  }

  getMassiveExclusionFormatUrl(companyId, insuranceCompanyId) {
    return `${baseURL}${apiPrefix}${v2}/ticket-groups/massive-exclusion/download-format?company=${companyId}&insurance=${insuranceCompanyId}`;
  }

  async createMassiveExclusion(payload) {
    const response = await this.client.post(
      `${apiPrefix}${v2}/ticket-groups/massive-exclusion`,
      payload
    );
    return response.data;
  }

  async preSuccessMassiveExclusion(ticketGroupId) {
    const response = await this.client.post(
      `${apiPrefix}${v2}/ticket-groups/${ticketGroupId}/exclusion/pre-success`
    );
    return response.data;
  }

  async successMassiveExclusion(ticketGroupId, payload) {
    const response = await this.client.post(
      `${apiPrefix}${v2}/ticket-groups/${ticketGroupId}/exclusion/success`,
      payload
    );
    return response.data;
  }

  async cancelMassiveExclusion(ticketGroupId, payload) {
    const response = await this.client.post(
      `${apiPrefix}${v2}/ticket-groups/${ticketGroupId}/exclusion/cancel`,
      payload
    );
    return response.data;
  }

  async updateMassiveExclusionEndDate(ticketGroupId, endDate) {
    const response = await this.client.post(
      `${apiPrefix}${v2}/ticket-groups/${ticketGroupId}/exclusion/update-end-date`,
      { endDate }
    );
    return response.data;
  }

  async createDependentsIncorporationFile(payload) {
    const response = await this.client.post(
      `${apiPrefix}${v2}/ticket-groups/file-dependents-incorporation`,
      payload
    );
    return response.data;
  }

  async createDependentsIncorporationForm(payload) {
    const response = await this.client.post(
      `${apiPrefix}${v2}/ticket-groups/massive-form-dependents-incorporation`,
      payload
    );
    return response.data;
  }

  async createDependentsIncorporationNoDeliverable(payload) {
    const response = await this.client.post(
      `${apiPrefix}${v2}/ticket-groups/no-deliverable-dependents-incorporation`,
      payload
    );
    return response.data;
  }

  getDependentsIncorporationFormatUrl(companyId, insuranceCompanyId, typeForm) {
    return `${baseURL}${apiPrefix}${v2}/ticket-groups/massive-dependents-incorporation/download-format?company=${companyId}&insurance=${insuranceCompanyId}&type=${typeForm}`;
  }

  async preSuccessDependentsIncorporation(ticketGroupId) {
    const response = await this.client.post(
      `${apiPrefix}${v2}/ticket-groups/${ticketGroupId}/dependants-incorporation/pre-success`
    );
    return response.data;
  }

  async successDependentsIncorporation(ticketGroupId, payload) {
    const response = await this.client.post(
      `${apiPrefix}${v2}/ticket-groups/${ticketGroupId}/dependants-incorporation/success`,
      payload
    );
    return response.data;
  }

  getMassiveDependentsExclusionFormatUrl(companyId, insuranceCompanyId) {
    return `${baseURL}${apiPrefix}${v2}/ticket-groups/massive-dependents-exclusion/download-format?company=${companyId}&insurance=${insuranceCompanyId}`;
  }

  async createMassiveDependentsExclusion(payload) {
    const response = await this.client.post(
      `${apiPrefix}${v2}/ticket-groups/massive-dependents-exclusion`,
      payload
    );
    return response.data;
  }

  async getMassiveDependentsExclusionFiles(ticketGroupId) {
    const response = await this.client.get(
      `${apiPrefix}${v2}/ticket-groups/${ticketGroupId}/dependants-exclusion/files`
    );
    return response.data;
  }

  async preSuccessMassiveDependentsExclusion(ticketGroupId) {
    const response = await this.client.post(
      `${apiPrefix}${v2}/ticket-groups/${ticketGroupId}/dependants-exclusion/pre-success`
    );
    return response.data;
  }

  async successMassiveDependentsExclusion(ticketGroupId, payload) {
    const response = await this.client.post(
      `${apiPrefix}${v2}/ticket-groups/${ticketGroupId}/dependants-exclusion/success`,
      payload
    );
    return response.data;
  }

  async preCancelMassiveDependentsExclusion(ticketGroupId) {
    const response = await this.client.post(
      `${apiPrefix}${v2}/ticket-groups/${ticketGroupId}/dependants-exclusion/pre-cancel`
    );
    return response.data;
  }

  async cancelMassiveDependentsExclusion(ticketGroupId, payload) {
    const response = await this.client.post(
      `${apiPrefix}${v2}/ticket-groups/${ticketGroupId}/dependants-exclusion/cancel`,
      payload
    );
    return response.data;
  }
}
